import React, { useState } from "react";
import { Grid, TextField, Button, Select, MenuItem, FormControl, InputLabel, FormHelperText } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDataProvider, useNotify, useRedirect, TopToolbar } from "react-admin";
import { Loader } from "../../Loader";
import { useForm, Controller } from "react-hook-form";

export const CreateHourlyBonusWheel = () => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const rewardTypes = [
    { id: 1, name: "Crowns" },
    { id: 2, name: "Gems" },
    { id: 3, name: "Good Luck" },
  ];

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      cooldownHours: "1",
      minValue: "0",
      maxValue: "10000",
      slots: Array(8).fill().map(() => ({ amount: "0", type: "3" })),
    },
  });

  const onSubmit = async (formData) => {
    const filledSlots = formData.slots.map((slot) => ({
      Type: parseInt(slot.type, 10),
      Amount: parseInt(slot.amount, 10),
    }));

    if (filledSlots.length !== 8) {
      notify("Exactly 8 slots are required.", { type: "error" });
      return;
    }

    const payload = {
      cooldownHours: parseInt(formData.cooldownHours, 10),
      minValue: parseInt(formData.minValue, 10),
      maxValue: parseInt(formData.maxValue, 10),
      slots: filledSlots,
    };

    setLoading(true);
    try {
      const response = await dataProvider.create("HourlyBonusWheel", {
        data: payload,
      });
      if (response) {
        notify("Hourly Bonus Configuration created successfully!", { type: "success" });
        redirect("/HourlyBonusWheel");
      }
    } catch (error) {
      notify(error.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      {loading && <Loader />}
      <TopToolbar>
        <Button
          label="Back"
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          sx={{
            mt: 3,
            background: "#FFC107",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#FFA000",
            },
          }}
          onClick={() => redirect("/HourlyBonusWheel")}
        >
          Back
        </Button>
      </TopToolbar>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ mt: 3 }}>
          {/* Cooldown Hours */}
          <Grid item xs={12} sm={4}>
            <Controller
              name="cooldownHours"
              control={control}
              rules={{ required: "Cooldown Hours is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Cooldown (Hours)"
                  fullWidth
                  error={!!errors.cooldownHours}
                  helperText={errors.cooldownHours?.message}
                  onKeyDown={(e) => {
                    const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
                    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    autoComplete: "off",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              )}
            />
          </Grid>

          {/* Minimum VC Per Reward */}
          <Grid item xs={12} sm={4}>
            <Controller
              name="minValue"
              control={control}
              rules={{ required: "Minimum VC Per Reward is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Minimum VC Per Reward"
                  fullWidth
                  error={!!errors.minValue}
                  helperText={errors.minValue?.message}
                  onKeyDown={(e) => {
                    const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
                    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    autoComplete: "off",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              )}
            />
          </Grid>

          {/* Maximum VC Per Reward */}
          <Grid item xs={12} sm={4}>
            <Controller
              name="maxValue"
              control={control}
              rules={{ required: "Maximum VC Per Reward is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Maximum VC Per Reward"
                  fullWidth
                  error={!!errors.maxValue}
                  helperText={errors.maxValue?.message}
                  onKeyDown={(e) => {
                    const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
                    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    autoComplete: "off",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              )}
            />
          </Grid>

          {/* Slots Configuration */}
          <Grid item xs={12}>
            <h3 style={{ color: "#FFC107", margin: "16px 0" }}>Slots Configuration (Must be exactly 8)</h3>
          </Grid>

          {Array.from({ length: 8 }).map((_, index) => (
            <Grid item xs={12} key={`slot-${index}`}>
              <Grid container spacing={2}>
                {/* Reward Type */}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`slots.${index}.type`}
                    control={control}
                    rules={{ required: "Reward Type is required" }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.slots?.[index]?.type}>
                        <InputLabel>{`Slot ${index + 1} - Reward Type`}</InputLabel>
                        <Select
                          {...field}
                          label={`Slot ${index + 1} - Reward Type`}
                          value={field.value || ""}
                        >
                          {rewardTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.slots?.[index]?.type && (
                          <FormHelperText>{errors.slots?.[index]?.type?.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* Amount */}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`slots.${index}.amount`}
                    control={control}
                    rules={{ required: "Amount is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={`Slot ${index + 1} - Amount`}
                        fullWidth
                        error={!!errors.slots?.[index]?.amount}
                        helperText={errors.slots?.[index]?.amount?.message}
                        onKeyDown={(e) => {
                          const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
                          if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        inputProps={{
                          autoComplete: "off",
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              sx={{
                background: "#FFC107",
                color: "#0A0A0A",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#FFA000",
                },
              }}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </React.Fragment>
  );
};
